import React from 'react';
import ReactQuill from 'react-quill';
import { HiBriefcase, HiOutlineOfficeBuilding, HiOutlineLocationMarker, HiOutlineLink, HiCheck } from 'react-icons/hi';
import useCreateJobFacade from '../facades/useCreateJobFacade';
import TitleWIthDescription from '../components/TitleWithDesc/TitleWIthDescription';
import 'react-quill/dist/quill.snow.css';

const quillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["bold", "italic", "underline"],
    [{ color: [] }],
    [{ align: [] }],
    ["link", "image"],
  ],
};

const quillFormats = [
  "header", "font", "size", "bold", "italic", "underline",
  "list", "bullet", "align", "link", "image", "color",
];

const CreateJob = () => {
  const { formData, handleChange, handleQuillChange, handleSubmit } = useCreateJobFacade();

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="pt-6">
        <TitleWIthDescription
          title1="Post an"
          title2="Opportunity"
          description="Share job or internship openings to help peers land their dream roles."
        />
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-[2rem] p-6 sm:p-10 shadow-xl border border-slate-100">
          
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-100">
            <div className="bg-blue-100 p-3 rounded-2xl text-blue-600">
              <HiBriefcase size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">Job Details</h2>
              <p className="text-slate-500 text-sm font-medium">Provide the specifics of the opportunity</p>
            </div>
          </div>

          <div className="space-y-6">
            
            {/* Grid for basic info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              
              {/* Type Selection */}
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Opportunity Type</label>
                <div className="relative">
                  <select
                    name="tag" 
                    value={formData.tag} 
                    onChange={handleChange}
                    className="w-full bg-slate-50 border border-slate-200 text-slate-700 font-bold rounded-xl px-4 py-3.5 appearance-none focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all cursor-pointer"
                    required
                  >
                    <option value="" disabled>Select Type...</option>
                    <option value="Job">Full-time Job</option>
                    <option value="Internship">Internship</option>
                    <option value="Hackathon">Hackathon</option>
                    <option value="Job/Internship">Job / Internship</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Role Title</label>
                <input
                  type="text" 
                  name="title" 
                  value={formData.title} 
                  onChange={handleChange}
                  placeholder="e.g. Software Engineer Intern"
                  className="w-full bg-slate-50 border border-slate-200 text-slate-800 font-bold rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                  required
                />
              </div>

              {/* Company */}
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Company Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                    <HiOutlineOfficeBuilding size={18} />
                  </div>
                  <input
                    type="text" 
                    name="company" 
                    value={formData.company} 
                    onChange={handleChange}
                    placeholder="e.g. Google, TCS"
                    className="w-full bg-slate-50 border border-slate-200 text-slate-800 font-bold rounded-xl pl-11 pr-4 py-3 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                    required
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Location</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                    <HiOutlineLocationMarker size={18} />
                  </div>
                  <input
                    type="text" 
                    name="location" 
                    value={formData.location} 
                    onChange={handleChange}
                    placeholder="e.g. Bangalore or Remote"
                    className="w-full bg-slate-50 border border-slate-200 text-slate-800 font-bold rounded-xl pl-11 pr-4 py-3 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Description (Quill) */}
            <div className="pt-2">
              <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Job Description</label>
              <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden focus-within:ring-4 focus-within:ring-blue-500/10 focus-within:border-blue-500 transition-all">
                <ReactQuill
                  value={formData.description}
                  onChange={handleQuillChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Share details about the role, requirements, and responsibilities..."
                  theme="snow"
                  className="min-h-[250px] bg-white border-none"
                />
              </div>
            </div>

            {/* Apply Link */}
            <div className="pt-2">
              <label className="block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Application Link</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-blue-500">
                  <HiOutlineLink size={18} />
                </div>
                <input
                  type="url" 
                  name="applyLink" 
                  value={formData.applyLink} 
                  onChange={handleChange}
                  placeholder="https://company.com/careers/apply..."
                  className="w-full bg-blue-50/50 border border-blue-100 text-blue-700 font-bold rounded-xl pl-11 pr-4 py-3.5 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all placeholder:text-blue-300"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                className="w-full sm:w-auto bg-slate-900 hover:bg-blue-600 text-white font-black uppercase tracking-widest text-sm py-4 px-10 rounded-xl focus:outline-none focus:ring-4 focus:ring-slate-200 transition-all active:scale-95 flex items-center justify-center gap-2 shadow-xl shadow-slate-200"
              >
                <HiCheck size={18} />
                Publish Opportunity
              </button>
            </div>

          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateJob;
